/**
 * PlanCheckPerformanceTab — Permitting Hub → Plan Check Performance (2026-10-22, redesigned the
 * same day: Matt asked for it to read like an Apple product page, focused on the critical numbers,
 * with hover motion that invites exploring).
 *
 * The page answers one question, in order: how long does this city take, and is it within the 30
 * business days the state now sets? A one-sentence headline says it; three large figures carry it
 * (first review, reviews past 30 business days, time the city held the next move); the live AB 253
 * clocks show what is with the city right now; then every first review on one chart. Detail (the
 * comparison, every project, how it is measured) sits behind disclosures so it never competes
 * with the headline. Built to be opened in a meeting with the city.
 *
 * Read + navigate only. Measuring: services/planCheckPerformance.js. Clock: services/reviewClock.js.
 * Styles: `.aj-pcp-*` in planCheckPerformance.css (motion respects prefers-reduced-motion).
 * docs/PLAN_CHECK_PERFORMANCE.md.
 */
import { useEffect, useMemo, useState } from 'react'
import { loadPlanCheckData, summarize, agencyList, median, inPeriod } from '../services/planCheckPerformance.js'
import { loadReviewClocks } from '../services/reviewClock.js'
import { ReviewClockBar } from './ReviewClock.jsx'
import { ctx } from '../outline/ctx.js'
import './planCheckPerformance.css'

const STORE = 'aj:plancheck:perf'
const thisYear = new Date().getFullYear()
const PERIODS = [
  ['12m', 'Last 12 months'],
  [String(thisYear), String(thisYear)],
  [String(thisYear - 1), String(thisYear - 1)],
  ['all', 'All time'],
]
const LINE_30_BUS = 42   // 30 business days is six calendar weeks; the chart draws the line there

const fmtDate = s => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || ''); return m ? `${+m[2]}/${+m[3]}/${m[1].slice(2)}` : '' }
const fmtLong = s => s ? new Date(s + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''
const plural = (n, w) => n + ' ' + w + (n === 1 ? '' : 's')
/** "Laguna Beach Community Development Building Division" → "Laguna Beach". */
const cityOf = a => (a || '').split(/ Community Development| Building| Development Services| Planning| Inspections| Department| - |\(/)[0].trim() || a
const isBuilding = a => /building/i.test(a.type) || /building/i.test(a.agency)

function readStore() { try { return JSON.parse(localStorage.getItem(STORE) || '{}') } catch { return {} } }

function openProject(projectId) {
  if (!projectId) return
  ctx.closeAllWorkspaces()
  ctx.zoomToProject(projectId)
  if (ctx.zoomToRoot) setTimeout(() => ctx.zoomToRoot('permits'), 0)
}

/** Counts up to `value` once per change (instant under reduced motion). */
function useCountUp(value, ms = 750) {
  const [v, setV] = useState(value == null ? null : 0)
  useEffect(() => {
    if (value == null) { setV(null); return }
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setV(value); return }
    let raf, t0
    const step = t => { if (!t0) t0 = t; const p = Math.min(1, (t - t0) / ms); setV(Math.round(value * (1 - Math.pow(1 - p, 3)))); if (p < 1) raf = requestAnimationFrame(step) }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [value, ms])
  return v
}

export default function PlanCheckPerformanceTab() {
  const stored = readStore()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [agency, setAgency] = useState(stored.agency || '')
  const [buildingOnly, setBuildingOnly] = useState(stored.buildingOnly !== false)
  const [period, setPeriod] = useState(stored.period || '12m')
  const [open, setOpen] = useState({ compare: false, projects: false, method: false })

  function load(refresh) {
    setError(''); if (refresh) setData(null)
    loadPlanCheckData({ refresh }).then(setData).catch(e => { setError(e.message || 'Could not load permit activity.'); setData([]) })
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(false); loadReviewClocks().catch(() => {}) }, [])
  useEffect(() => { try { localStorage.setItem(STORE, JSON.stringify({ agency, buildingOnly, period })) } catch { /* ignore */ } }, [agency, buildingOnly, period])

  const agencies = useMemo(() => {
    const all = agencyList(data || [])
    return buildingOnly ? all.filter(isBuilding) : all
  }, [data, buildingOnly])
  const current = agencies.some(a => a.agency === agency) ? agency : (agencies[0] && agencies[0].agency) || ''
  const sum = useMemo(() => (data && current ? summarize(data, current, period) : null), [data, current, period])
  const periodLabel = PERIODS.find(p => p[0] === period)[1]
  const city = cityOf(current)
  const toggle = k => setOpen(o => ({ ...o, [k]: !o[k] }))

  return (
    <div className="aj-pcp" style={{ height: '100%', overflowY: 'auto' }}>
      <div className="aj-pcp-page">
        <div className="aj-pcp-controls">
          <label className="aj-pcp-picker">
            <select value={current} onChange={e => setAgency(e.target.value)} disabled={!agencies.length}>
              {agencies.map(a => <option key={a.agency} value={a.agency}>{a.agency}</option>)}
            </select>
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 3.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
          </label>
          <label className="aj-pcp-check"><input type="checkbox" checked={buildingOnly} onChange={e => setBuildingOnly(e.target.checked)} /> Building departments only</label>
          <span style={{ flex: 1 }} />
          <div className="aj-pcp-seg">
            {PERIODS.map(([k, l]) => <button key={k} className={period === k ? 'on' : ''} onClick={() => setPeriod(k)}>{l}</button>)}
          </div>
          <button className="aj-pcp-refresh" title="Reload from the activity log" onClick={() => load(true)}>
            <svg width="13" height="13" viewBox="0 0 14 14" aria-hidden="true"><path d="M12 7a5 5 0 1 1-1.5-3.6M12 2v3h-3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>

        {error && <div className="aj-pcp-error">{error}</div>}
        {data == null && <Loading />}
        {data && !agencies.length && !error && <div className="aj-pcp-muted">No measured reviews yet.</div>}

        {sum && (sum.rounds.length === 0
          ? <div className="aj-pcp-hero"><div className="aj-pcp-eyebrow">{current}</div><h1>No review rounds started in this period.</h1><p>Choose a longer period above.</p></div>
          : <Page key={current + period} sum={sum} data={data} current={current} city={city} period={period} periodLabel={periodLabel} buildingOnly={buildingOnly} open={open} toggle={toggle} />)}
      </div>
    </div>
  )
}

function Loading() {
  return (
    <div>
      <div className="aj-pcp-skel" style={{ width: 180, height: 12 }} />
      <div className="aj-pcp-skel" style={{ width: '70%', height: 34, marginTop: 16 }} />
      <div className="aj-pcp-skel" style={{ width: '45%', height: 18, marginTop: 12 }} />
      <div style={{ display: 'flex', gap: 16, marginTop: 36 }}>
        {[0, 1, 2].map(i => <div key={i} className="aj-pcp-skel" style={{ flex: 1, height: 170, borderRadius: 18 }} />)}
      </div>
      <div className="aj-pcp-muted" style={{ marginTop: 18 }}>Reading every agency review and its activity log…</div>
    </div>
  )
}

function Page({ sum, data, current, city, period, periodLabel, buildingOnly, open, toggle }) {
  const firsts = sum.rounds.filter(r => r.kind === 'first' && !(!r.open && r.agencyDays === 0))
  const finishedFirsts = firsts.filter(r => !r.open)
  const share = sum.agencyShare == null ? null : Math.round(sum.agencyShare * 100)
  const openNow = [...sum.openNow].sort((a, b) => b.agencyDays - a.agencyDays)

  // The headline's number is red when the typical first review runs past 30 business days.
  const over = sum.firstBusMedian != null && sum.firstBusMedian > 30
  const num = <em className={over ? 'over' : ''}>{sum.firstMedian} days</em>
  const headline = sum.firstMedian == null
    ? <>{city} has not returned a first review in this period.</>
    : sum.firstCount === 1 ? <>{city}'s one first review this period took {num}.</>
    : sum.firstCount < 4 ? <>{city}'s {sum.firstCount} first reviews this period took a median of {num}.</>
    : <>{city} returns a first review in {num}.</>
  const subParts = []
  if (sum.firstCount) subParts.push(sum.firstOver30Bus === 0 ? 'Every first review came back inside 30 business days.' : `${sum.firstOver30Bus} of ${sum.firstCount} ran past the 30 business days the state now sets.`)
  if (share != null) subParts.push(`The city held the next move ${share}% of the time.`)

  return (
    <>
      <section className="aj-pcp-hero aj-pcp-rise">
        <div className="aj-pcp-eyebrow">{current} · {periodLabel}</div>
        <h1>{headline}</h1>
        {subParts.length > 0 && <p>{subParts.join(' ')}</p>}
      </section>

      <section className="aj-pcp-figures">
        <FigureFirst value={sum.firstMedian} bus={sum.firstBusMedian} count={sum.firstCount} rounds={finishedFirsts} />
        <FigureOver over={sum.firstOver30Bus} count={sum.firstCount} />
        <FigureShare share={share} agencyDays={sum.agencyDays} ourDays={sum.ourDays} />
      </section>

      <section className="aj-pcp-strip aj-pcp-rise" style={{ animationDelay: '240ms' }}>
        <Small label="Recheck" value={sum.recheckMedian} unit="days" tip={sum.recheckCount ? `Median of ${plural(sum.recheckCount, 'recheck')}, resubmittal to the next comments.` : 'No rechecks finished in this period.'} />
        <Small label="Our turnaround" value={sum.ourTurnaroundMedian} unit="days" tip="Median days from their comments to our resubmittal." />
        <Small label="Our follow-ups" value={sum.totalChecks} unit="" tip={sum.checksPerRound != null ? `Status checks we made while the city held the review, a median of ${sum.checksPerRound} per round.` : 'Status checks we made while the city held the review.'} />
        <Small label="Approvals" value={sum.approvals} unit="" tip="Reviews approved in this period." />
      </section>

      {openNow.length > 0 && (
        <section className="aj-pcp-section aj-pcp-rise" style={{ animationDelay: '320ms' }}>
          <h2>With {city} right now</h2>
          <div className="aj-pcp-now">
            {openNow.map(r => (
              <div key={r.review.id + r.start} className="aj-pcp-card aj-pcp-nowcard">
                <div className="aj-pcp-nowtop">
                  <button className="aj-pcp-link" onClick={() => openProject(r.review.projectId)}>{r.review.projectName}</button>
                  <span className="aj-pcp-pill">{r.kind === 'first' ? 'First review' : r.kind === 'revision' ? 'Permit revision' : 'Recheck'}</span>
                </div>
                <div className="aj-pcp-nowbig"><span>{r.agencyDays}</span> days with the city</div>
                <div className="aj-pcp-nowsub">Submitted {fmtLong(r.start)} · {r.businessDays} business days · {plural(r.checks, 'follow-up')}</div>
                {r.kind === 'first' && <div style={{ marginTop: 14 }}><ReviewClockBar reviewId={r.review.id} reviewName={r.review.agency} /></div>}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="aj-pcp-section aj-pcp-rise" style={{ animationDelay: '400ms' }}>
        <h2>Every first review</h2>
        <p className="aj-pcp-lede">Days from our submittal to their first comments. The shaded band is longer than 30 business days.</p>
        <div className="aj-pcp-card aj-pcp-chartcard"><FirstReviewChart rounds={firsts} /></div>
      </section>

      {(sum.stale.length > 0 || sum.otcCount > 0 || sum.revisionCount > 0) && (
        <p className="aj-pcp-fine">
          {sum.otcCount > 0 || sum.revisionCount > 0 ? `Not in these numbers: ${[sum.otcCount && plural(sum.otcCount, 'same-day review'), sum.revisionCount && plural(sum.revisionCount, 'permit revision review')].filter(Boolean).join(' and ')}. ` : ''}
          {sum.stale.length > 0 ? `${plural(sum.stale.length, 'review')} with no outcome logged in over four months ${sum.stale.length === 1 ? 'is' : 'are'} left out (${sum.stale.map(x => x.review.projectName).join(', ')}).` : ''}
        </p>
      )}

      <Disclosure title={`How ${city} compares`} open={open.compare} onToggle={() => toggle('compare')}>
        <CompareChart data={data} period={period} current={current} buildingOnly={buildingOnly} />
      </Disclosure>
      <Disclosure title={`All ${plural(sum.reviews.length, 'project')}`} open={open.projects} onToggle={() => toggle('projects')}>
        <ProjectTable summary={sum} />
      </Disclosure>
      <Disclosure title="How these are measured" open={open.method} onToggle={() => toggle('method')}>
        <p className="aj-pcp-method">
          Every number comes from the dated entries in our permit activity log. A round starts the day we submit or resubmit
          and ends the day we receive comments or approval. When comments arrive department by department, the round runs to
          the last of them. Days the city spends waiting on items it requested from us count as ours. "The next move" adds up
          every day it was theirs against every day it was ours. Same-day reviews and permit revisions stay out of the
          medians. Business days skip weekends but not holidays. A round counts in the period it started in.
        </p>
      </Disclosure>
    </>
  )
}

/* ── The three figures ─────────────────────────────────────────────── */

function FigureFirst({ value, bus, count, rounds }) {
  const v = useCountUp(value)
  const max = Math.max(LINE_30_BUS + 14, ...rounds.map(r => r.agencyDays))
  return (
    <div className="aj-pcp-card aj-pcp-fig aj-pcp-rise" style={{ animationDelay: '80ms' }}>
      <div className="aj-pcp-figlabel">First review</div>
      <div className="aj-pcp-fignum">{v == null ? '—' : v}<span>days</span></div>
      <div className="aj-pcp-figsub">{count ? `median of ${count} · ${bus} business days` : 'none finished yet'}</div>
      <div className="aj-pcp-dots" aria-hidden="true">
        <i className="aj-pcp-line30" style={{ left: `${LINE_30_BUS / max * 100}%` }} />
        {rounds.map((r, i) => (
          <b key={i} title={`${r.review.projectName}: ${r.agencyDays} days`} style={{ left: `${r.agencyDays / max * 100}%`, animationDelay: `${300 + i * 35}ms`, background: r.businessDays > 30 ? '#c0392b' : 'var(--aj-accent)' }} />
        ))}
      </div>
    </div>
  )
}

function FigureOver({ over, count }) {
  const v = useCountUp(count ? over : null)
  const pct = count ? over / count : 0
  const [drawn, setDrawn] = useState(0)
  useEffect(() => { const t = setTimeout(() => setDrawn(pct), 60); return () => clearTimeout(t) }, [pct])
  const R = 44, C = 2 * Math.PI * R
  return (
    <div className="aj-pcp-card aj-pcp-fig aj-pcp-rise" style={{ animationDelay: '140ms' }}>
      <div className="aj-pcp-figlabel">Past 30 business days</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
        <svg width="108" height="108" viewBox="0 0 108 108" className="aj-pcp-ring">
          <circle cx="54" cy="54" r={R} fill="none" stroke="var(--aj-bg-muted)" strokeWidth="9" />
          <circle cx="54" cy="54" r={R} fill="none" stroke={pct > 0 ? '#c0392b' : 'var(--aj-accent)'} strokeWidth="9" strokeLinecap="round"
            strokeDasharray={C} strokeDashoffset={C * (1 - drawn)} transform="rotate(-90 54 54)"
            style={{ transition: 'stroke-dashoffset 1.1s cubic-bezier(.2,.7,.2,1)', opacity: drawn > 0 ? 1 : 0 }} />
          <text x="54" y="60" textAnchor="middle" className="aj-pcp-ringtxt">{count ? Math.round(pct * 100) + '%' : '—'}</text>
        </svg>
        <div>
          <div className="aj-pcp-fignum" style={{ fontSize: 44 }}>{v == null ? '—' : v}<span>of {count || 0}</span></div>
          <div className="aj-pcp-figsub">first reviews, submittal to first comments</div>
        </div>
      </div>
    </div>
  )
}

function FigureShare({ share, agencyDays, ourDays }) {
  const v = useCountUp(share)
  const [w, setW] = useState(0)
  useEffect(() => { const t = setTimeout(() => setW(share || 0), 60); return () => clearTimeout(t) }, [share])
  return (
    <div className="aj-pcp-card aj-pcp-fig aj-pcp-rise" style={{ animationDelay: '200ms' }}>
      <div className="aj-pcp-figlabel">The next move was theirs</div>
      <div className="aj-pcp-fignum">{v == null ? '—' : v}<span>%</span></div>
      <div className="aj-pcp-figsub">{agencyDays} days with the city · {ourDays} with us</div>
      <div className="aj-pcp-split">
        <div style={{ width: `${w}%` }} className="aj-pcp-split-them" title={`${agencyDays} days with the city`} />
        <div style={{ flex: 1 }} className="aj-pcp-split-us" title={`${ourDays} days with us`} />
      </div>
      <div className="aj-pcp-splitlegend"><span>City</span><span>Alder James</span></div>
    </div>
  )
}

function Small({ label, value, unit, tip }) {
  return (
    <div className="aj-pcp-small" tabIndex={0}>
      <div className="aj-pcp-smalllabel">{label}</div>
      <div className="aj-pcp-smallnum">{value == null ? '—' : value}{value != null && unit ? <span> {value === 1 ? unit.replace(/s$/, '') : unit}</span> : null}</div>
      <div className="aj-pcp-tip">{tip}</div>
    </div>
  )
}

function Disclosure({ title, open, onToggle, children }) {
  return (
    <section className={'aj-pcp-disc' + (open ? ' open' : '')}>
      <button className="aj-pcp-disc-head" onClick={onToggle} aria-expanded={open}>
        <span>{title}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M3 4.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
      </button>
      {open && <div className="aj-pcp-disc-body">{children}</div>}
    </section>
  )
}

/* ── Chart: every first review ─────────────────────────────────────── */

function FirstReviewChart({ rounds }) {
  const [hover, setHover] = useState(null)
  if (!rounds.length) return <div className="aj-pcp-muted">No first reviews in this period.</div>
  const W = 960, H = 280, L = 44, R = 18, T = 18, B = 30
  const t = s => new Date(s + 'T12:00:00').getTime()
  const xs = rounds.map(r => t(r.start))
  let x0 = Math.min(...xs), x1 = Math.max(...xs)
  if (x1 - x0 < 60 * 864e5) { x0 -= 30 * 864e5; x1 += 30 * 864e5 }
  const pad = (x1 - x0) * 0.04; x0 -= pad; x1 += pad
  const yMax = Math.max(LINE_30_BUS + 14, ...rounds.map(r => r.agencyDays)) * 1.1
  const X = v => L + (v - x0) / (x1 - x0) * (W - L - R)
  const Y = v => T + (1 - v / yMax) * (H - T - B)
  const step = yMax > 240 ? 60 : yMax > 120 ? 30 : yMax > 60 ? 15 : 10
  const ticks = []; for (let v = 0; v <= yMax; v += step) ticks.push(v)
  const span = (x1 - x0) / 864e5
  const every = span > 900 ? 6 : span > 400 ? 3 : 1
  const months = []
  const d = new Date(x0); d.setDate(1); d.setMonth(d.getMonth() + 1)
  while (d.getTime() <= x1) { if (d.getMonth() % every === 0) months.push(new Date(d)); d.setMonth(d.getMonth() + 1) }
  const h = hover != null ? rounds[hover] : null
  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} className={'aj-pcp-chart' + (h ? ' hovering' : '')} onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id="pcpOver" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#c0392b" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#c0392b" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <rect x={L} y={T} width={W - L - R} height={Math.max(0, Y(LINE_30_BUS) - T)} fill="url(#pcpOver)" rx="6" />
        {ticks.map(v => (
          <g key={v}>
            <line x1={L} x2={W - R} y1={Y(v)} y2={Y(v)} className="aj-pcp-grid" />
            <text x={L - 8} y={Y(v) + 3} textAnchor="end" className="aj-pcp-axis">{v}</text>
          </g>
        ))}
        {months.map(m => (
          <text key={m.getTime()} x={X(m.getTime())} y={H - 8} textAnchor="middle" className="aj-pcp-axis">
            {m.toLocaleString('en-US', { month: 'short' })}{m.getMonth() === 0 || every > 1 ? ' ' + String(m.getFullYear()).slice(2) : ''}
          </text>
        ))}
        <line x1={L} x2={W - R} y1={Y(LINE_30_BUS)} y2={Y(LINE_30_BUS)} className="aj-pcp-line" />
        <text x={L + 8} y={Y(LINE_30_BUS) - 7} className="aj-pcp-linelabel">30 business days</text>
        {h && <line x1={X(t(h.start))} x2={X(t(h.start))} y1={Y(h.agencyDays) + 8} y2={H - B} className="aj-pcp-guide" />}
        {rounds.map((r, i) => {
          const over = r.businessDays > 30
          const cx = X(t(r.start)), cy = Y(r.agencyDays)
          const c = over ? '#c0392b' : 'var(--aj-accent)'
          return (
            <g key={i} className={'aj-pcp-dot' + (hover === i ? ' on' : '')} style={{ animationDelay: `${450 + i * 40}ms`, transformOrigin: `${cx}px ${cy}px` }}
              onMouseEnter={() => setHover(i)} onClick={() => openProject(r.review.projectId)}>
              <circle cx={cx} cy={cy} r="16" fill="transparent" />
              <circle cx={cx} cy={cy} r="12" className="aj-pcp-halo" fill={c} />
              <circle cx={cx} cy={cy} r="6" fill={r.open ? 'var(--aj-bg-surface)' : c} stroke={c} strokeWidth={r.open ? 2.2 : 0} />
            </g>
          )
        })}
      </svg>
      {h && (
        <div className={'aj-pcp-tipcard' + (X(t(h.start)) / W < 0.18 ? ' left' : X(t(h.start)) / W > 0.82 ? ' right' : '')} style={{ left: `${X(t(h.start)) / W * 100}%`, top: `${Y(h.agencyDays) / H * 100}%` }}>
          <div className="aj-pcp-tipname">{h.review.projectName}</div>
          <div className="aj-pcp-tipnum">{h.agencyDays} <span>days{h.open ? ' so far' : ''}</span></div>
          <div className="aj-pcp-tipsub">{h.businessDays} business days · submitted {fmtDate(h.start)}{h.end ? ` · comments ${fmtDate(h.end)}` : ''}</div>
          {h.checks > 0 && <div className="aj-pcp-tipsub">{plural(h.checks, 'follow-up')} from us</div>}
        </div>
      )}
      <div className="aj-pcp-legend">
        <span><i style={{ background: 'var(--aj-accent)' }} />Within 30 business days</span>
        <span><i style={{ background: '#c0392b' }} />Past 30 business days</span>
        <span><i className="ring" />Still in review</span>
      </div>
    </div>
  )
}

/* ── Disclosures ───────────────────────────────────────────────────── */

function CompareChart({ data, period, current, buildingOnly }) {
  const list = agencyList(data).filter(a => !buildingOnly || isBuilding(a))
  const rows = list.map(a => {
    const firsts = data.filter(r => r.agency === a.agency).flatMap(r => r.rounds).filter(x => x.kind === 'first' && !x.open && x.agencyDays > 0 && inPeriod(x.start, period))
    return { agency: a.agency, n: firsts.length, med: median(firsts.map(x => x.agencyDays)) }
  }).filter(r => (r.n >= 2 || r.agency === current) && r.med != null).sort((a, b) => a.med - b.med)
  if (!rows.length) return <div className="aj-pcp-muted">Not enough finished first reviews in this period to compare.</div>
  const max = Math.max(...rows.map(r => r.med), LINE_30_BUS)
  return (
    <div className="aj-pcp-compare">
      {rows.map((r, i) => (
        <div key={r.agency} className={'aj-pcp-crow' + (r.agency === current ? ' me' : '')} title={r.agency}>
          <span className="aj-pcp-cname">{cityOf(r.agency)}</span>
          <span className="aj-pcp-ctrack">
            <i className="aj-pcp-cline" style={{ left: `${LINE_30_BUS / max * 100}%` }} />
            <span className="aj-pcp-cbar" style={{ width: `${r.med / max * 100}%`, animationDelay: `${i * 60}ms` }} />
          </span>
          <span className="aj-pcp-cnum">{r.med}<small> days</small></span>
          <span className="aj-pcp-cn">{plural(r.n, 'review')}</span>
        </div>
      ))}
      <div className="aj-pcp-muted" style={{ marginTop: 8 }}>Median days to first comments. The thin line marks about 30 business days.</div>
    </div>
  )
}

function ProjectTable({ summary }) {
  const rows = summary.reviews.map(r => ({ r, start: r.rounds[0] ? r.rounds[0].start : '' })).sort((a, b) => b.start.localeCompare(a.start))
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="aj-pcp-table">
        <thead><tr><th>Project</th><th>Permit</th><th className="n">Submitted</th><th className="n">Rounds, days with the city</th><th className="n">City</th><th className="n">Us</th><th className="n">Follow-ups</th><th className="n">Approved</th></tr></thead>
        <tbody>
          {rows.map(({ r }) => (
            <tr key={r.id} onClick={() => openProject(r.projectId)}>
              <td>{[r.projectNumber, r.projectName].filter(Boolean).join(' · ')}</td>
              <td className="m">{r.permitType}</td>
              <td className="n">{fmtDate(r.firstSubmitted)}</td>
              <td className="n">{r.rounds.map((x, i) => (
                <span key={i} className={'aj-pcp-round' + (x.open ? ' open' : '') + (x.businessDays > 30 && x.kind === 'first' ? ' over' : '')}
                  title={`${x.kind === 'first' ? 'First review' : x.kind === 'revision' ? 'Permit revision' : 'Recheck'} from ${fmtDate(x.start)}${x.stale ? ', no outcome logged' : x.open ? ', still in review' : ''}`}>{x.stale ? '?' : x.agencyDays}</span>
              ))}</td>
              <td className="n">{r.agencyDays}</td>
              <td className="n">{r.ourDays}</td>
              <td className="n">{r.rounds.reduce((s, x) => s + x.checks, 0)}</td>
              <td className="n">{r.approvedOn ? fmtDate(r.approvedOn) : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
